import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronUp, ChevronRight, X } from 'lucide-react';
import RatingStars from './RatingStars';
import RangeSlider from './RangeSlider';
import { formatPrice } from '../../utils/priceCalculator';

// Sidebar de filtros do catálogo. Estado é controlado externamente via
// props (`filters`) e mudanças são reportadas via `onChange(partial)`.
// Categorias e marcas vêm de fora — derivadas dos produtos carregados ou
// de endpoints dedicados.
const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-app-border last:border-0 py-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left text-sm font-semibold text-app-text"
      >
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

const RATING_OPTIONS = [
  { value: 4, label: '4 estrelas ou mais' },
  { value: 3, label: '3 estrelas ou mais' },
  { value: 2, label: '2 estrelas ou mais' },
  { value: 1, label: '1 estrela ou mais' },
];

// Coleta ids de todos os ancestrais do `currentId` na árvore — usados pra
// auto-expandir os nós no carregamento inicial quando o usuário acessou via
// link com ?categoryId=<id-folha>.
function collectAncestors(nodes, currentId, ancestors = []) {
  for (const n of nodes || []) {
    if (String(n.id) === String(currentId)) return ancestors;
    if (Array.isArray(n.children) && n.children.length) {
      const found = collectAncestors(n.children, currentId, [...ancestors, n.id]);
      if (found) return found;
    }
  }
  return null;
}

const CategoryNode = ({ node, depth, currentId, onSelect, expandedIds, toggleExpand }) => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isActive = String(currentId) === String(node.id);
  return (
    <li>
      <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 12}px` }}>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => toggleExpand(node.id)}
            className="p-0.5 rounded hover:bg-app-surface-hover text-app-text-secondary"
            aria-label={isExpanded ? 'Recolher' : 'Expandir'}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="inline-block w-[18px]" />
        )}
        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className={`flex-1 text-sm text-left px-2 py-1 rounded flex items-center justify-between ${
            isActive
              ? 'bg-primary/10 text-primary font-semibold'
              : 'text-app-text hover:bg-app-surface-hover'
          }`}
        >
          <span className="truncate">{node.name}</span>
          {node.count != null && (
            <span className="text-xs text-app-text-secondary ml-2">({node.count})</span>
          )}
        </button>
      </div>
      {hasChildren && isExpanded && (
        <ul className="mt-1 space-y-1">
          {node.children.map((child) => (
            <CategoryNode
              key={child.id}
              node={child}
              depth={depth + 1}
              currentId={currentId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// Drill-down tree de categorias. Aceita lista flat (cada item com parentId)
// ou árvore (cada item com children). Auto-expande caminho até a categoria
// selecionada para que ela esteja visível ao reabrir a página.
const CategoryTree = ({ categories, currentId, onSelect }) => {
  // Normaliza pra árvore se vier flat (tolera ambos os shapes).
  const tree = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    const isAlreadyTree = categories.some((c) => Array.isArray(c.children));
    if (isAlreadyTree) return categories;
    const map = new Map(categories.map((c) => [c.id, { ...c, children: [] }]));
    const roots = [];
    for (const c of map.values()) {
      if (c.parentId && map.has(c.parentId)) {
        map.get(c.parentId).children.push(c);
      } else {
        roots.push(c);
      }
    }
    return roots;
  }, [categories]);

  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // Auto-expande ancestrais quando a categoria atual mudar via URL.
  useEffect(() => {
    if (!currentId) return;
    const ancestors = collectAncestors(tree, currentId);
    if (ancestors && ancestors.length) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [currentId, tree]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (tree.length === 0) {
    return <p className="text-xs text-app-text-secondary px-2">Sem categorias</p>;
  }

  return (
    <ul className="space-y-1 max-h-72 overflow-y-auto">
      <li>
        <button
          type="button"
          onClick={() => onSelect('')}
          className={`text-sm w-full text-left px-2 py-1 rounded ${
            !currentId
              ? 'bg-primary/10 text-primary font-semibold'
              : 'text-app-text hover:bg-app-surface-hover'
          }`}
        >
          Todas
        </button>
      </li>
      {tree.map((node) => (
        <CategoryNode
          key={node.id}
          node={node}
          depth={0}
          currentId={currentId}
          onSelect={onSelect}
          expandedIds={expandedIds}
          toggleExpand={toggleExpand}
        />
      ))}
    </ul>
  );
};

const CatalogFilters = ({
  filters,
  onChange,
  categories = [],
  brands = [],
  priceRange = [0, 1000],
  onClear,
}) => {
  const [brandQuery, setBrandQuery] = useState('');
  const [priceLocal, setPriceLocal] = useState([
    Number(filters.minPrice ?? priceRange[0]),
    Number(filters.maxPrice ?? priceRange[1]),
  ]);

  useEffect(() => {
    setPriceLocal([
      Number(filters.minPrice ?? priceRange[0]),
      Number(filters.maxPrice ?? priceRange[1]),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.minPrice, filters.maxPrice, priceRange[0], priceRange[1]]);

  const filteredBrands = useMemo(() => {
    if (!brandQuery.trim()) return brands;
    const q = brandQuery.toLowerCase();
    return brands.filter((b) => b.toLowerCase().includes(q));
  }, [brands, brandQuery]);

  const selectedBrands = useMemo(() => {
    if (!filters.brand) return new Set();
    return new Set(String(filters.brand).split(',').map((b) => b.trim()).filter(Boolean));
  }, [filters.brand]);

  const toggleBrand = (b) => {
    const next = new Set(selectedBrands);
    if (next.has(b)) next.delete(b);
    else next.add(b);
    onChange({ brand: next.size ? Array.from(next).join(',') : '' });
  };

  const setRating = (v) => {
    onChange({ minRating: filters.minRating === v ? '' : v });
  };

  const hasAny = !!(
    filters.q || filters.categoryId || filters.brand || filters.minRating
    || filters.minPrice != null || filters.maxPrice != null
  );

  return (
    <aside className="w-full lg:sticky lg:top-32">
      <div className="bg-white rounded-lg border border-app-border p-4">
        <div className="flex items-center justify-between pb-2">
          <h3 className="font-bold text-app-text">Filtros</h3>
          {hasAny && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
            >
              <X size={12} /> Limpar
            </button>
          )}
        </div>

        <Section title="Categorias">
          <CategoryTree
            categories={categories}
            currentId={filters.categoryId}
            onSelect={(id) => onChange({ categoryId: id })}
          />
        </Section>

        <Section title="Preço">
          <RangeSlider
            min={priceRange[0]}
            max={priceRange[1]}
            value={priceLocal}
            onChange={setPriceLocal}
            onCommit={(v) => onChange({ minPrice: v[0], maxPrice: v[1] })}
            format={formatPrice}
          />
        </Section>

        {brands.length > 0 && (
          <Section title="Marca">
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-app-text-secondary" />
              <input
                type="search"
                value={brandQuery}
                onChange={(e) => setBrandQuery(e.target.value)}
                placeholder="Buscar marca..."
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-app-border rounded focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {filteredBrands.length === 0 ? (
                <li className="text-xs text-app-text-secondary">Nenhuma marca</li>
              ) : (
                filteredBrands.map((b) => (
                  <li key={b}>
                    <label className="flex items-center gap-2 px-1 py-1 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedBrands.has(b)}
                        onChange={() => toggleBrand(b)}
                        className="rounded border-app-border"
                      />
                      {b}
                    </label>
                  </li>
                ))
              )}
            </ul>
          </Section>
        )}

        <Section title="Avaliação">
          <ul className="space-y-1.5">
            {RATING_OPTIONS.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => setRating(opt.value)}
                  className={`flex items-center gap-2 px-1 py-1 rounded w-full text-left ${
                    Number(filters.minRating) === opt.value
                      ? 'bg-primary/10'
                      : 'hover:bg-app-surface-hover'
                  }`}
                >
                  <RatingStars rating={opt.value} count={1} hideCount hideIfEmpty={false} size={12} />
                  <span className="text-xs text-app-text-secondary">e mais</span>
                </button>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </aside>
  );
};

export default CatalogFilters;
